import { forwardRef, RefObject } from 'react';

import {
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Spacer,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { giftingTopUpWalletFormSchema } from 'app/utils/schemas/gifting/giftingTopUpWalletForm';

import { useTopUpWalletStore } from 'app/store';

import getPointsValue from 'app/utils/points';

import { formatCurrency } from 'app/utils/strings';

import {
  ArrowLeftOutline,
  ArrowRightOutline,
  CrossOutline,
  InfoCircleOutline,
} from '@aslanpay/portal-common-lib';

import { TopUpWalletFields, TopUpWalletProps } from '../types';

const GiftingTopUpWalletForm = forwardRef<HTMLElement, TopUpWalletProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const topUpWalletData = useTopUpWalletStore((state) => state.topUpWalletData);
    const setTopUpWalletData = useTopUpWalletStore((state) => state.setTopUpWalletData);

    const {
      register,
      handleSubmit,
      watch,
      resetField,
      setFocus,
      formState: { errors, isValid },
    } = useForm<TopUpWalletFields>({
      mode: 'onChange',
      defaultValues: topUpWalletData as TopUpWalletFields,
      resolver: yupResolver(giftingTopUpWalletFormSchema),
    });

    const watchPointsValue = watch('pointsAmount');

    const handleNextStep = (data: TopUpWalletFields) => {
      setTopUpWalletData(data);
      onStepAction();
    };

    const resetAndFocusField = (field: keyof TopUpWalletFields) => {
      resetField(field, { defaultValue: '' });
      setFocus(field);
    };

    return (
      <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
        <Stack spacing="20px">
          <HStack spacing="20px" alignItems="flex-start">
            <FormControl isInvalid={!!errors?.pointsAmount?.message} isRequired>
              <FormLabel>Please enter points amount</FormLabel>
              <InputGroup>
                <Input
                  type="text"
                  borderColor="#5934C4"
                  placeholder="Provide points amount in multiples of 1,000"
                  {...register('pointsAmount')}
                />
                {watchPointsValue && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Clear points value"
                      icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                      variant="link"
                      onClick={() => resetAndFocusField('pointsAmount')}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage>{errors?.pointsAmount?.message}</FormErrorMessage>
            </FormControl>
          </HStack>
          <VStack align="start" spacing={2}>
            <Text fontSize="12px" fontWeight="400">
              This will cost you
            </Text>

            <Spacer />
            <Box
              backgroundColor="brand.lightGray2"
              borderColor="brand.lightGray1Alpha40"
              border="1px solid"
              p="4"
              w="100%"
              borderRadius="8"
            >
              <HStack>
                watchPointsValue !== undefined && watchPointsValue !== {Number('')} &&
                !isNaN(Number(watchPointsValue)) && (
                <Text fontSize="16px" fontWeight="400" align="left">
                  {formatCurrency(getPointsValue(Number(watchPointsValue) || 0), 'GBP')}
                </Text>
                )
                <Spacer />
                <Text fontSize="14px" color="#31314E" fontWeight="400" align="right">
                  Rate: 200 points = £1
                </Text>
              </HStack>
              <Divider my={2} />
              <HStack>
                <InfoCircleOutline />
                <Text color="#392080" fontSize="12px" fontWeight="400" align="left">
                  1,000 points would cost you £5. Your employee can redeem the same 1,000 for £5
                  cash back spend.
                </Text>
              </HStack>
            </Box>
          </VStack>
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={() => previousStep()}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

GiftingTopUpWalletForm.displayName = 'TopUpWalletForm';

export default GiftingTopUpWalletForm;
